import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8093/api';

export interface SystemLoginRequest {
  username: string;
  password: string;
  source?: string;
}

export interface GoogleLoginRequest {
  googleIdToken: string;
  source?: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface AuthResponse {
  userId: number;
  name: string;
  email: string | null;
  username: string | null;
  userType: 'CLIENT' | 'SYSTEM';
  roles: string[];
  privileges: string[];
  accessToken: string;
  refreshToken: string;
  isPremium: boolean;
}

export interface UserProfile {
  userId: number;
  name: string;
  email: string | null;
  profilePhoto: string | null;
  userType: 'CLIENT' | 'SYSTEM';
  roles: string[];
  privileges: string[];
  createdDate: string | null;
}

export interface SubscriptionResponse {
  isPremium: boolean;
  planName: string | null;
  planPrice: number | null;
  startDate: string | null;
  expiryDate: string | null;
  daysRemaining: number;
  status: string;
  createdDate: string | null;
}

export interface CommunityUserStatsResponse {
  totalUsers: number;
  premiumUsers: number;
  freeUsers: number;
  newThisWeek: number;
  newThisMonth: number;
  expiredToday: number;
  totalRevenue: number;
}

export interface CommunityAdminUser {
  id: number;
  name: string | null;
  email: string | null;
  joinedAt: string | null;
  authProvider: 'GOOGLE' | 'LOCAL' | string;
  premium: boolean;
  planName: string | null;
  subStart: string | null;
  subExpiry: string | null;
  amountPaid: number | null;
}

export interface UpdateProfileRequest {
  name: string;
  profilePhotoUrl: string;
}

export interface PlanResponse {
  id: number;
  planName: string;
  durationMonths: number;
  price: number;
  originalPrice: number;
  currency: string;
  discountPercent: number;
  description: string;
  features: string[];
  isActive: boolean;
  product: string;
}

export interface ApiResponse<T> {
  data: T;
  status: string;
  message: string;
  errors: any;
}

export interface TutorialResponse {
  id: number;
  title: string;
  description: string;
  badge: string;
  tags: string[];
  orderIndex: number;
  isFree: boolean;
  isActive: boolean;
  videoCount: number;
}

export interface CommunityPlanAdminRequest {
  planName: string;
  durationMonths: number;
  price: number;
  originalPrice: number;
  discountPercent?: number;
  description?: string;
  featuresDescription?: string;
  isActive?: boolean;
}

export interface BacktestSummary {
  winRate?: number | null;
  totalTrades?: number | null;
  avgReturn?: number | null;
  maxDrawdown?: number | null;
  period?: string | null;
}

export interface CommunityScreenerResponse {
  id: number;
  name: string;
  description: string;
  tags: string[];
  timeframe: string;
  chartinkScreenerId: string | null;
  isFree: boolean;
  isActive: boolean;
  orderIndex: number;
  relatedTutorials?: TutorialResponse[];
  backtestSummary?: BacktestSummary | null;
  backtestFileUrl?: string | null;
  pineScript?: string | null;
  screenerPrompt?: string | null;
  screenerPromptDesc?: string | null;
}

export type AnnouncementTag = 'INFO' | 'IMPORTANT' | 'WARNING';

export interface AnnouncementResponse {
  id: number;
  title: string;
  content: string;
  tag: AnnouncementTag;
  isPinned: boolean;
  isActive: boolean;
  videoUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AnnouncementRequest {
  title: string;
  content: string;
  tag: AnnouncementTag;
  isPinned: boolean;
  isActive?: boolean;
  videoUrl?: string | null;
}

export interface ChatMessageResponse {
  id: number;
  userId: number;
  content: string;
  senderType: 'USER' | 'ADMIN';
  isRead: boolean;
  createdAt: string;
}

export interface ChatConversationSummary {
  userId: number;
  userName: string;
  userEmail: string | null;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  lastActiveAt: string | null;
}

export interface CreateScreenerRequest {
  name: string;
  description: string;
  tags: string;
  timeframe: string;
  chartinkScreenerId?: string;
  orderIndex: number;
  isFree: boolean;
  relatedTutorialIds?: string;
  backtestSummary?: BacktestSummary | null;
  backtestFileUrl?: string;
  pineScript?: string;
  screenerPrompt?: string;
  screenerPromptDesc?: string;
}

export interface VideoResponse {
  id: number;
  tutorialId: number;
  title: string;
  description: string;
  videoUrl: string | null;
  durationMin: number;
  orderIndex: number;
  locked: boolean;
}

export interface CreatePaymentOrderRequest {
  planId: number;
}

export interface PaymentOrderResponse {
  orderId: string;
  amount: number;
  currency: string;
  receipt: string;
  key: string;
  userEmail: string;
  userName: string;
  userContact: string;
}

export interface VerifyPaymentRequest {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

export interface PaymentVerificationResponse {
  success: boolean;
  transactionId: string;
  subscriptionId: number;
  planName: string;
  expiryDate: string;
  daysRemaining: number;
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await authApi.refreshToken({ refreshToken });
          localStorage.setItem('accessToken', response.data.accessToken);
          localStorage.setItem('refreshToken', response.data.refreshToken);
          originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        authApi.clearTokens();
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  async systemLogin(credentials: SystemLoginRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/auth/system/login', {
      ...credentials,
      source: 'COMMUNITY',
    });
    return response.data;
  },

  async googleLogin(request: GoogleLoginRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await apiClient.post<ApiResponse<AuthResponse>>('/auth/google/login', {
      ...request,
      source: 'COMMUNITY',
    });
    return response.data;
  },

  async refreshToken(request: RefreshTokenRequest): Promise<ApiResponse<{ accessToken: string; refreshToken: string }>> {
    const response = await apiClient.post<ApiResponse<{ accessToken: string; refreshToken: string }>>('/auth/refresh-token', request);
    return response.data;
  },

  async logout(): Promise<ApiResponse<null>> {
    const token = localStorage.getItem('accessToken');
    const response = await axios.post<ApiResponse<null>>(
      `${API_BASE_URL}/auth/logout`, {},
      { headers: { Authorization: `Bearer ${token}` } }
    );
    this.clearTokens();
    return response.data;
  },

  async getUserProfile(): Promise<ApiResponse<UserProfile>> {
    const response = await apiClient.get<ApiResponse<UserProfile>>('/user/profile');
    return response.data;
  },

  async updateUserProfile(request: UpdateProfileRequest): Promise<ApiResponse<null>> {
    const response = await apiClient.put<ApiResponse<null>>('/user/profile', request);
    return response.data;
  },

  async getSubscription(): Promise<ApiResponse<SubscriptionResponse>> {
    const response = await apiClient.get<ApiResponse<SubscriptionResponse>>('/user/subscription', {
      headers: { 'X-Product': 'COMMUNITY' },
    });
    return response.data;
  },

  // ── Admin: tutorials ──────────────────────────────────────────────────────
  async adminGetTutorials(): Promise<ApiResponse<TutorialResponse[]>> {
    const response = await apiClient.get<ApiResponse<TutorialResponse[]>>('/admin/community/tutorials');
    return response.data;
  },

  async adminCreateTutorial(data: {
    title: string; description: string; badge: string;
    tags: string; orderIndex: number; isFree: boolean;
  }): Promise<ApiResponse<TutorialResponse>> {
    const response = await apiClient.post<ApiResponse<TutorialResponse>>('/admin/community/tutorials', data);
    return response.data;
  },

  async adminUpdateTutorial(id: number, data: Partial<{
    title: string; description: string; badge: string;
    tags: string; orderIndex: number; isFree: boolean;
  }>): Promise<ApiResponse<TutorialResponse>> {
    const response = await apiClient.put<ApiResponse<TutorialResponse>>(`/admin/community/tutorials/${id}`, data);
    return response.data;
  },

  async adminToggleTutorial(id: number): Promise<ApiResponse<null>> {
    const response = await apiClient.patch<ApiResponse<null>>(`/admin/community/tutorials/${id}/toggle`);
    return response.data;
  },

  async adminToggleTutorialFree(id: number): Promise<ApiResponse<null>> {
    const response = await apiClient.patch<ApiResponse<null>>(`/admin/community/tutorials/${id}/toggle-free`);
    return response.data;
  },

  async adminDeleteTutorial(id: number): Promise<ApiResponse<null>> {
    const response = await apiClient.delete<ApiResponse<null>>(`/admin/community/tutorials/${id}`);
    return response.data;
  },

  async adminGetVideos(tutorialId: number): Promise<ApiResponse<VideoResponse[]>> {
    const response = await apiClient.get<ApiResponse<VideoResponse[]>>(`/admin/community/tutorials/${tutorialId}/videos`);
    return response.data;
  },

  async adminAddVideo(tutorialId: number, data: {
    title: string; description: string; videoUrl: string; durationMin: number; orderIndex: number;
  }): Promise<ApiResponse<VideoResponse>> {
    const response = await apiClient.post<ApiResponse<VideoResponse>>(`/admin/community/tutorials/${tutorialId}/videos`, data);
    return response.data;
  },

  async adminUpdateVideo(videoId: number, data: Partial<{
    title: string; description: string; videoUrl: string; durationMin: number; orderIndex: number;
  }>): Promise<ApiResponse<VideoResponse>> {
    const response = await apiClient.put<ApiResponse<VideoResponse>>(`/admin/community/videos/${videoId}`, data);
    return response.data;
  },

  async adminDeleteVideo(videoId: number): Promise<ApiResponse<null>> {
    const response = await apiClient.delete<ApiResponse<null>>(`/admin/community/videos/${videoId}`);
    return response.data;
  },

  async adminUploadVideo(file: File): Promise<ApiResponse<string>> {
    const form = new FormData();
    form.append('file', file);
    const token = localStorage.getItem('accessToken');
    const response = await axios.post<ApiResponse<string>>(
      `${API_BASE_URL}/admin/community/upload`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  async adminGetUserStats(): Promise<ApiResponse<CommunityUserStatsResponse>> {
    const response = await apiClient.get<ApiResponse<CommunityUserStatsResponse>>('/admin/community/user-stats');
    return response.data;
  },

  async adminGetCommunityUsers(): Promise<ApiResponse<CommunityAdminUser[]>> {
    const response = await apiClient.get<ApiResponse<CommunityAdminUser[]>>('/admin/community/users');
    return response.data;
  },

  async adminUploadFile(file: File): Promise<ApiResponse<string>> {
    const form = new FormData();
    form.append('file', file);
    const token = localStorage.getItem('accessToken');
    const response = await axios.post<ApiResponse<string>>(
      `${API_BASE_URL}/admin/community/upload-file`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
    );
    return response.data;
  },

  async getScreeners(): Promise<ApiResponse<CommunityScreenerResponse[]>> {
    const response = await apiClient.get<ApiResponse<CommunityScreenerResponse[]>>('/community/screeners');
    return response.data;
  },

  async getScreenerById(id: number): Promise<ApiResponse<CommunityScreenerResponse>> {
    const response = await apiClient.get<ApiResponse<CommunityScreenerResponse>>(`/community/screeners/${id}`);
    return response.data;
  },

  async adminGetScreeners(): Promise<ApiResponse<CommunityScreenerResponse[]>> {
    const response = await apiClient.get<ApiResponse<CommunityScreenerResponse[]>>('/admin/community/screeners');
    return response.data;
  },

  async adminCreateScreener(data: CreateScreenerRequest): Promise<ApiResponse<CommunityScreenerResponse>> {
    const response = await apiClient.post<ApiResponse<CommunityScreenerResponse>>('/admin/community/screeners', data);
    return response.data;
  },

  async adminUpdateScreener(id: number, data: Partial<CreateScreenerRequest>): Promise<ApiResponse<CommunityScreenerResponse>> {
    const response = await apiClient.put<ApiResponse<CommunityScreenerResponse>>(`/admin/community/screeners/${id}`, data);
    return response.data;
  },

  async adminToggleScreener(id: number): Promise<ApiResponse<null>> {
    const response = await apiClient.patch<ApiResponse<null>>(`/admin/community/screeners/${id}/toggle`);
    return response.data;
  },

  async adminToggleScreenerFree(id: number): Promise<ApiResponse<null>> {
    const response = await apiClient.patch<ApiResponse<null>>(`/admin/community/screeners/${id}/toggle-free`);
    return response.data;
  },

  async adminDeleteScreener(id: number): Promise<ApiResponse<null>> {
    const response = await apiClient.delete<ApiResponse<null>>(`/admin/community/screeners/${id}`);
    return response.data;
  },

  async getTutorials(): Promise<ApiResponse<TutorialResponse[]>> {
    const response = await axios.get<ApiResponse<TutorialResponse[]>>(`${API_BASE_URL}/community/tutorials`);
    return response.data;
  },

  async getTutorialVideos(tutorialId: number): Promise<ApiResponse<VideoResponse[]>> {
    const response = await apiClient.get<ApiResponse<VideoResponse[]>>(`/community/tutorials/${tutorialId}/videos`);
    return response.data;
  },

  async getPlans(product?: string): Promise<ApiResponse<PlanResponse[]>> {
    const url = product ? `${API_BASE_URL}/plans?product=${product}` : `${API_BASE_URL}/plans`;
    const response = await axios.get<ApiResponse<PlanResponse[]>>(url);
    return response.data;
  },

  async createPaymentOrder(request: CreatePaymentOrderRequest): Promise<ApiResponse<PaymentOrderResponse>> {
    const response = await apiClient.post<ApiResponse<PaymentOrderResponse>>('/payments/create-order', request);
    return response.data;
  },

  async verifyPayment(request: VerifyPaymentRequest): Promise<ApiResponse<PaymentVerificationResponse>> {
    const response = await apiClient.post<ApiResponse<PaymentVerificationResponse>>('/payments/verify', request);
    return response.data;
  },

  // ── Chat ─────────────────────────────────────────────────────────────────
  async getChatMessages(): Promise<ApiResponse<ChatMessageResponse[]>> {
    const response = await apiClient.get<ApiResponse<ChatMessageResponse[]>>('/chat/messages');
    return response.data;
  },

  async sendChatMessage(content: string): Promise<ApiResponse<ChatMessageResponse>> {
    const response = await apiClient.post<ApiResponse<ChatMessageResponse>>('/chat/messages', { content });
    return response.data;
  },

  async getChatUnreadCount(): Promise<ApiResponse<{ unreadCount: number; adminLastActiveAt: string | null }>> {
    const response = await apiClient.get<ApiResponse<{ unreadCount: number; adminLastActiveAt: string | null }>>('/chat/unread-count');
    return response.data;
  },

  async adminGetUnreadCount(): Promise<ApiResponse<number>> {
    const response = await apiClient.get<ApiResponse<number>>('/admin/chat/unread-count');
    return response.data;
  },

  async adminGetConversations(): Promise<ApiResponse<ChatConversationSummary[]>> {
    const response = await apiClient.get<ApiResponse<ChatConversationSummary[]>>('/admin/chat/conversations');
    return response.data;
  },

  async adminGetConversation(userId: number): Promise<ApiResponse<ChatMessageResponse[]>> {
    const response = await apiClient.get<ApiResponse<ChatMessageResponse[]>>(`/admin/chat/conversations/${userId}`);
    return response.data;
  },

  async adminReply(userId: number, content: string): Promise<ApiResponse<ChatMessageResponse>> {
    const response = await apiClient.post<ApiResponse<ChatMessageResponse>>(`/admin/chat/conversations/${userId}/reply`, { content });
    return response.data;
  },

  // ── Announcements ─────────────────────────────────────────────────────────

  async getAnnouncements(): Promise<ApiResponse<AnnouncementResponse[]>> {
    const response = await apiClient.get<ApiResponse<AnnouncementResponse[]>>('/community/announcements');
    return response.data;
  },

  async adminGetAnnouncements(): Promise<ApiResponse<AnnouncementResponse[]>> {
    const response = await apiClient.get<ApiResponse<AnnouncementResponse[]>>('/admin/community/announcements');
    return response.data;
  },

  async adminCreateAnnouncement(req: AnnouncementRequest): Promise<ApiResponse<AnnouncementResponse>> {
    const response = await apiClient.post<ApiResponse<AnnouncementResponse>>('/admin/community/announcements', req);
    return response.data;
  },

  async adminUpdateAnnouncement(id: number, req: AnnouncementRequest): Promise<ApiResponse<AnnouncementResponse>> {
    const response = await apiClient.put<ApiResponse<AnnouncementResponse>>(`/admin/community/announcements/${id}`, req);
    return response.data;
  },

  async adminDeleteAnnouncement(id: number): Promise<ApiResponse<void>> {
    const response = await apiClient.delete<ApiResponse<void>>(`/admin/community/announcements/${id}`);
    return response.data;
  },

  // ── Community badges ──────────────────────────────────────────────────────
  async getCommunityBadges(): Promise<ApiResponse<{ UPDATES: number; SCREENERS: number; LEARNING: number }>> {
    const response = await apiClient.get<ApiResponse<{ UPDATES: number; SCREENERS: number; LEARNING: number }>>('/community/badges');
    return response.data;
  },

  async markCommunitySectionSeen(section: 'UPDATES' | 'SCREENERS' | 'LEARNING'): Promise<void> {
    await apiClient.post(`/community/badges/mark-seen/${section}`);
  },

  // ── Community Plan Admin ──────────────────────────────────────────────────
  async adminGetCommunityPlans(): Promise<ApiResponse<PlanResponse[]>> {
    const response = await apiClient.get<ApiResponse<PlanResponse[]>>('/admin/community/plans');
    return response.data;
  },

  async adminCreateCommunityPlan(req: CommunityPlanAdminRequest): Promise<ApiResponse<PlanResponse>> {
    const response = await apiClient.post<ApiResponse<PlanResponse>>('/admin/community/plans', req);
    return response.data;
  },

  async adminUpdateCommunityPlan(id: number, req: CommunityPlanAdminRequest): Promise<ApiResponse<PlanResponse>> {
    const response = await apiClient.put<ApiResponse<PlanResponse>>(`/admin/community/plans/${id}`, req);
    return response.data;
  },

  async adminDeactivateCommunityPlan(id: number): Promise<ApiResponse<void>> {
    const response = await apiClient.delete<ApiResponse<void>>(`/admin/community/plans/${id}`);
    return response.data;
  },

  async adminReactivateCommunityPlan(id: number): Promise<ApiResponse<void>> {
    const response = await apiClient.patch<ApiResponse<void>>(`/admin/community/plans/${id}/reactivate`);
    return response.data;
  },

  saveTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
  },

  clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  },

  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  },

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  },
};

export default authApi;
